<template>
    <v-dialog v-model="visible" width="500">
        <template #activator="{ props: activatorProps }">
            <v-list-item v-bind="activatorProps" @click="open">
                <v-list-item-title @click="burgermenuActive = false" v-text="$t('archiv.table.burgermenu.edit')" />
            </v-list-item>
        </template>
        <form @submit.prevent="submit">
            <v-card>
                <FocusLock>
                    <v-card-title class="headline font-weight-bold mb-2">
                        {{ $t('archiv.editdialog.title') }}
                    </v-card-title>
                    <v-card-text class="pb-0">
                        <v-text-field
                            v-model="dokumentCopy.bezeichnung"
                            autofocus
                            :label="$t('archiv.table.header.dokumentname')"
                            :placeholder="$t('archiv.table.header.dokumentname')"
                        ></v-text-field>
                        <v-combobox
                            v-if="dokumentTyp == 'archiv.dokumenttyp.eigenesdok' || dokumentTyp == 'archiv.dokumenttyp.buchhaltung'"
                            v-model="selectedSchlagworte"
                            v-model:search="schlagwortSearchString"
                            class="mt-3"
                            :items="schlagwortDropdownList"
                            :label="$t('upload.table.schlagworte')"
                            :hide-no-data="!schlagwortSearchString"
                            bg-color="white"
                            hide-selected
                            solo
                            multiple
                            :clear-on-select="true"
                            :custom-filter="customFilter"
                            @update:model-value="onSelectedSchlagworteChanged"
                            @keydown.space.prevent="simulateEnter"
                        >
                            <template #selection="{ attrs, item, selected, index }">
                                <v-chip
                                    v-bind="attrs"
                                    :input-value="selected"
                                    label
                                    small
                                    closable
                                    @click:close="deleteSchlagwortFromSelection(index, item.raw)"
                                >
                                    <span class="pr-2">
                                        {{ item.value }}
                                    </span>
                                </v-chip>
                            </template>
                            <template #item="{ props, item, index }">
                                <v-list-item v-if="item.raw.header && search">
                                    <span class="mr-3">Create</span>
                                    <v-chip size="small" variant="flat" label>
                                        {{ search }}
                                    </v-chip>
                                </v-list-item>
                                <v-list-subheader v-else-if="item.raw.header" :title="item.title"></v-list-subheader>
                                <v-list-item v-else @click="props.onClick">
                                    <v-chip :text="item.raw" variant="outlined"></v-chip>
                                    <template #append>
                                        <v-btn
                                            icon="mdi-delete"
                                            size="small"
                                            variant="text"
                                            @click.stop.prevent="deleteSchlagwortFromDropdown(index, item.raw)"
                                        ></v-btn>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-combobox>
                        <v-text-field
                            v-else
                            v-model="dokumentCopy.schlagworte"
                            :label="$t('archiv.table.header.beschreibung')"
                            :placeholder="$t('archiv.table.header.beschreibung')"
                            readonly
                            disabled
                        ></v-text-field>
                        <v-text-field
                            :label="$t('archiv.editdialog.dateiname')"
                            :placeholder="$t('archiv.editdialog.dateiname')"
                            :model-value="fullDokumentName"
                            readonly
                            disabled
                        ></v-text-field>
                    </v-card-text>

                    <v-card-actions class="ml-4">
                        <v-btn type="submit" color="primary" variant="flat" class="modalbutton mb-3">
                            {{ $t('components.dialog.save') }}
                        </v-btn>
                        <v-btn class="modalbutton mb-3" variant="flat" @click="visible = false">
                            {{ $t('components.dialog.cancel') }}
                        </v-btn>
                    </v-card-actions>
                </FocusLock>
            </v-card>
        </form>
    </v-dialog>
</template>
<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-facing-decorator';
import { DokumentEditApiDto } from '@/api-schema';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';

import { NAME as NAME_ARCHIV } from '../store';
const archivModul = namespace(NAME_ARCHIV);

import { CREATE_SCHLAGWORT, DELETE_SCHLAGWORT, EDIT_DOKUMENT_INFO, GET_DOKUMENT, GET_SCHLAGWORTE } from '../store/actions';
import { SCHLAGWORTVORSCHLAEGE } from '../store/getters';

@Component
export default class EditDokumentMetadataDialog extends Vue {
    @Prop({ required: true, type: Number })
    private dokumentId!: number;
    @Prop({ required: true, type: String })
    private fullDokumentName!: string;
    @Prop({ required: true, type: String })
    private dokumentTyp!: string;

    visible = false;
    private dokumentCopy: DokumentEditApiDto = {};
    schlagwortSearchString = '';
    schlagwortEditText = '';
    selectedSchlagworte = [];

    @archivModul.Action(EDIT_DOKUMENT_INFO) edit!: Function;
    @archivModul.Action(GET_DOKUMENT) getDokument!: Function;
    @archivModul.Action(GET_SCHLAGWORTE) getSchlagwortVorschlaege!: Function;
    @archivModul.Action(CREATE_SCHLAGWORT) createSchlagwort!: Function;
    @archivModul.Action(DELETE_SCHLAGWORT) deleteSchlagwort!: Function;
    @archivModul.Getter(SCHLAGWORTVORSCHLAEGE)
    schlagwortDropdownList!: Array<string>;

    private onSave() {
        this.edit(this.dokumentCopy).then(() => {
            this.visible = false;
        });
    }

    submit() {
        this.onSave();
    }

    /**
     * Diese Methode entfernt ein Schlagwort wieder aus der Auswahl,
     * Ausgelöst wenn bei einem Chip das kreuz angeklickt wird.
     */
    deleteSchlagwortFromSelection(index, item) {
        if (index > -1) {
            this.schlagwortDropdownList.splice(index, 1);
            this.selectedSchlagworte.splice(index, 1);
            this.dokumentCopy.schlagworte = this.selectedSchlagworte.join(' ');
        }
    }

    /**
     * Diese Methode löscht ein Schlagwort aus der Datenbank und aus dem Dropdown.
     * Ausgelöst durch einen Klick auf das Abfalleimer - Symbol im Dropdown
     */
    deleteSchlagwortFromDropdown(index, item) {
        this.deleteSchlagwort(item);
        // vorschlagliste updaten
        if (this.schlagwortSearchString) {
            this.getSchlagwortVorschlaege(this.schlagwortSearchString);
        }
    }

    private open() {
        this.getDokument(this.dokumentId).then((response: any) => {
            this.dokumentCopy = Object.assign({}, response);
            // Schlagworte werden nur bei Buchhaltungs- und eigenen Dokumenten behandelt
            if (this.dokumentTyp === 'archiv.dokumenttyp.eigenesdok' || 'archiv.dokumenttyp.buchhaltung' === this.dokumentTyp) {
                this.selectedSchlagworte = [];
                this.schlagwortSearchString = '';
                if (this.dokumentCopy.schlagworte != null) {
                    this.schlagwortSearchString = this.dokumentCopy.schlagworte;
                    const multiSchlagworte = this.dokumentCopy.schlagworte.split(' ').filter((s) => s.trim() !== '');
                    for (const schlagwort of multiSchlagworte) {
                        this.selectedSchlagworte.push(schlagwort);
                    }
                    this.dokumentCopy.schlagworte = this.selectedSchlagworte.join('');
                }
            }
            this.visible = true;
        });
    }

    /**
     * Diese Methode fügt ein neues Schlagwort sowohl zum Dokument,
     * als allenfalls auch zur Datenbank, sofern es neu ist.
     *
     */
    onSelectedSchlagworteChanged(schlagworteString: any) {
        const existingKeywords = this.dokumentCopy.schlagworte ? this.dokumentCopy.schlagworte.split(' ') : [];
        const hasNewKeyword = schlagworteString.some((keyword) => !existingKeywords.includes(keyword.trim()));
        if (!hasNewKeyword) {
            return; // Exit if no new keyword is detected
        }

        const newEntry = schlagworteString.at(-1);
        // Suchstrings mit wildcards werden nur für suche verwendet, aber nie als schlagwort erstellt
        if (typeof newEntry === 'string' && newEntry.includes('*')) {
            this.selectedSchlagworte.pop();
            return;
        }

        this.dokumentCopy.schlagworte = this.selectedSchlagworte.join(' ');

        if (this.schlagwortDropdownList.includes(schlagworteString.at(-1))) {
            // das schlagwort existiert bereits im Dropdown.
            return;
        }
        // Wenn  es ein neues Schlagwort ist, fügen wir es in die DB ein
        if (schlagworteString.at(-1)) {
            this.createSchlagwort(schlagworteString.at(-1));
        }
    }

    /**
     * Diese Methode holt mithilfe des Suchstrings existierende Schlagwörter aus der Datenbank.
     * Quasi Autocompletion.
     *
     */
    @Watch('schlagwortSearchString')
    onSearchStringChanged() {
        this.bouncedSearchStringChange();
    }
    private bouncedSearchStringChange = debounce(function (this: EditDokumentMetadataDialog) {
        if (this.schlagwortSearchString != null) {
            if (this.schlagwortSearchString.trim() != '') {
                this.getSchlagwortVorschlaege(this.schlagwortSearchString);
            } else {
                // Dropdown - List resetten
                this.schlagwortDropdownList.length = 0;
            }
        }
    }, 400);

    simulateEnter(event: Event) {
        // Hack, damit bei 'Space' der gleiche Event ausgelöst wird wie bei 'Enter'.
        // Grund: Der Suchstring kann nicht manuell zurückgesetzt werden
        event.preventDefault();
        const enterEvent = new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            keyCode: 13,
            bubbles: true
        });

        event.target.dispatchEvent(enterEvent);
    }
    customFilter(item: string, queryText: string): boolean {
        // Ersetze "*" mit dem Regex-Platzhalter-Muster
        const searchText = queryText.replace(/\*/g, '.*');
        // Erstelle ein Regex-Muster und entferne Sonderzeichen ausser "*"
        const regex = new RegExp(searchText.replace(/[-/\\^$+?.()|[\]{}]/g, '\\$&'), 'i');
        return regex.test(item);
    }
}
</script>
